/**
 * @file Main native bridge class for both web and native.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AllReceivableMessages,
  GetMessages,
  GetPayloadConstructorFunction,
  GetRepliableMessages,
  ReplyWithErrorFunction,
  ReplyWithResultFunction,
  Values,
} from '../types/GetMessages'
import NativeBridgeManifest, {
  isPayloadWithReplyConstructor,
  NativeBridgePayloadOnlyConstructor,
  NativeBridgePayloadWithReplyConstructor,
} from '../types/NativeBridgeManifest'
import { isUndefinedValue, undefinedValue } from '../types/NativeBridgeValue'
import compactMapValues from '../utils/compactMapValues'
import mapValues from '../utils/mapValues'
import {
  deserializeMessageType,
  MessageTypeClassification,
  serializeMessageType,
} from '../utils/messageTypeSerialization'

/**
 * Internal message types that are not defined in the manifest. These messages
 * facilitate the 3-way handshake between the web and native bridges to ensure
 * both sides are ready.
 */
type InternalMessage =
  | { internalMessageType: 'webBridgeReady' } // web -> native
  | { internalMessageType: 'nativeBridgeReady' } // native -> web
  | { internalMessageType: 'bothBridgesReady' } // web -> native

/**
 * Generic signature of the message object dispatched across the bridge, before
 * any processing.
 */
type RawMessageContent = {
  messageType: string
  messageId?: number
  payload: unknown
}

type MessageListenerDynamicArgs<
  TManifest extends NativeBridgeManifest,
  TClient extends 'web' | 'native',
  TMessageType extends keyof GetMessages<TManifest, TClient, 'receiving'>,
> = TManifest[TClient extends 'web'
  ? 'nativeToWeb'
  : 'webToNative'][TMessageType] extends NativeBridgePayloadWithReplyConstructor
  ? [
      resolve: (
        ...args: Parameters<GetPayloadConstructorFunction<TManifest, TClient, 'receiving', TMessageType, 'result'>>
      ) => void,
      reject: (error: Error | { message: string }) => void,
    ]
  : []

const DEFAULT_REPLY_TIMEOUT_IN_MS = 10000

type NativeBridgeOptions = {
  /** @default 10000 */
  replyTimeoutInMs?: number
}

/**
 * Native bridge implementation, shared between both web and native. This class
 * shouldn't be instantiated and used directly, but rather through the
 * respective implementations inside `@padlet/native-bridge-web` and
 * `@padlet/native-bridge-native`.
 */
class NativeBridge<TManifest extends NativeBridgeManifest, TClient extends 'web' | 'native'> {
  // Properties
  private readonly options: Required<NativeBridgeOptions>

  /**
   * Mapping of message types to their respective payload factory functions. For
   * repliable messages, the payload factor is taken from the `payload`
   * property.
   */
  private readonly sendableMessageFactories: {
    [messageType: string]: NativeBridgePayloadOnlyConstructor
  }

  /**
   * A set of messages that require a reply. This is used to determine whether
   * we should send a `messageId` with the message, and whether we should expect
   * a reply by returning promise.
   */
  private readonly sendableMessageTypesRequiringReplies: Set<string>

  /**
   * Mapping of receivable message types to their respective reply factory
   * functions.  Only messages that expect a reply (i.e. those with a `reply`
   * key in their declaration in the manifest) are included in this mapping.
   */
  private readonly replyMessageFactories: {
    [messageType: string]: NativeBridgePayloadOnlyConstructor
  }

  /**
   * The type of the client, either `'web'` or `'native'`. This is used to
   * determine the types of messages that can be sent and received.
   */
  private readonly client: TClient

  /**
   * A mapping of sender IDs to their respective sender functions. The bridge
   * can have multiple senders and will forward the message to all of them.
   */
  private messageSenders: {
    [senderId: number]: (messageContent: object) => void
  } = {}

  /**
   * A mapping of message types to their respective listener functions.
   */
  private messageListeners: {
    [messageType: string]: {
      [listenerId: number]: (payload: any, ...args: any[]) => void
    }
  } = {}

  /**
   * A mapping of universal message listeners.
   */
  private universalMessageListeners: {
    [listenerId: number]: (
      message: Values<AllReceivableMessages<TManifest>>,
      replyWithResult: ReplyWithResultFunction<TManifest>,
      replyWithError: ReplyWithErrorFunction<TManifest>,
    ) => void
  } = {}

  /**
   * A mapping of message types to their respective pending reply resolvers.
   * This will only contain entries for messages that expect a reply.
   */
  private pendingReplyResolvers: {
    [messageType: string]: {
      [messageId: string]: {
        resolve(payload: any): void
        reject(error: any): void
        timeout: ReturnType<typeof setTimeout>
      }
    }
  } = {}

  /**
   * The auto-incrementing numerical ID for message senders. This is used to
   * uniquely identify each sender for ultimately removing them.
   */
  private senderIdCounter = 0

  /**
   * The counter for listener IDs. This is used to uniquely identify listener
   * functions for ultimately removing them.
   */
  private listenerIdCounter = 0

  /**
   * The counter for message IDs. This is used to uniquely identify messages
   * when processing replies.
   * @remarks This is only incremented and assigned to messages that expect a
   * reply. Normal messages don't need an ID.
   */
  private messageIdCounter = 0

  /** Whether the bridge is ready to send/receive messages. */
  private isReady = false

  /** Before the bridge is ready, messages are enqueued here before being sent off. */
  private messageQueue: { messageType: keyof GetMessages<TManifest, TClient, 'sending'>; payload: any }[] = []

  /**
   * Marks the web bridge as ready and begin the 3-way handshake process.
   * @remarks This method is only callable from web bridges. On native bridges,
   * this method is a no-op.
   */
  readonly ready!: TClient extends 'web' ? () => void : never

  // Initialization
  constructor(manifest: TManifest, client: TClient, options?: NativeBridgeOptions) {
    this.options = {
      replyTimeoutInMs: DEFAULT_REPLY_TIMEOUT_IN_MS,
      ...options,
    }

    this.sendableMessageFactories = mapValues(manifest[client === 'web' ? 'webToNative' : 'nativeToWeb'], (value) => {
      if (isPayloadWithReplyConstructor(value)) {
        return value.payload
      } else {
        return value as NativeBridgePayloadOnlyConstructor
      }
    }) as any

    this.sendableMessageTypesRequiringReplies = new Set(
      Object.entries(manifest[client === 'web' ? 'webToNative' : 'nativeToWeb'])
        .filter(([, value]) => isPayloadWithReplyConstructor(value))
        .map(([messageType]) => messageType),
    )

    this.replyMessageFactories = compactMapValues(
      manifest[client === 'web' ? 'nativeToWeb' : 'webToNative'],
      (value) => {
        return isPayloadWithReplyConstructor(value) ? value.reply : undefined
      },
    ) as any

    this.client = client

    // This is only callable from web bridges.
    this.ready = (
      client === 'web'
        ? (): void => {
            if (this.isReady) {
              // Avoid sending duplicate ready messages
              return
            }
            this.sendUnsafeRawMessage({ internalMessageType: 'webBridgeReady' })
          }
        : (): void => {
            console.warn('[NativeBridge] Only web native bridges can call prepareToReceiveMessages()')
          }
    ) as typeof this.ready
  }

  private processMessageContentForSending(content: unknown): unknown {
    // Process `undefined` values into a special type
    if (content === undefined) {
      return undefinedValue()
    }

    // Keep non-object values as is
    if (typeof content !== 'object' || content === null) {
      return content
    }

    // Recursively process array values
    if (Array.isArray(content)) {
      return content.map((element) => this.processMessageContentForSending(element))
    }

    // Recursively process object values
    return mapValues(content, (value) => this.processMessageContentForSending(value))
  }

  private processMessageContentForReceiving(content: unknown): unknown {
    // Keep non-object values as is
    if (typeof content !== 'object' || content === null) {
      return content
    }

    // Check for special values
    switch (true) {
      case isUndefinedValue(content):
        return undefined
    }

    // Recursively process array values
    if (Array.isArray(content)) {
      return content.map((element) => this.processMessageContentForReceiving(element))
    }

    // Recursively process object values
    return mapValues(content, (value) => this.processMessageContentForReceiving(value))
  }

  // Methods
  /**
   * Internal method to send a message to the other side. This should never be
   * called externally because it doesn't encode the message payload, and
   * bypasses ready checks.
   * @param messageContent An opaque object to be sent to the other side.
   * @remarks This method should only be used for debugging purposes. If you
   * find yourself using this function over `sendMessage` in production, you're
   * probably doing something wrong.
   */
  sendUnsafeRawMessage(content: object): void {
    const processedContent = this.processMessageContentForSending(content) as object
    Object.values(this.messageSenders).forEach((messageSender) => messageSender(processedContent))
  }

  private onReady(): void {
    this.isReady = true
    this.messageQueue.forEach((message) => this.sendUnsafeRawMessage(message))
    this.messageQueue.length = 0
  }

  private processInternalMessage(message: InternalMessage): void {
    // Convenient method to simplify the following switch statement
    const processMessageIfClientIs = (expectedClient: 'web' | 'native', processor: () => void): void => {
      if (this.client !== expectedClient) {
        console.warn(
          `[NativeBridge] Only ${expectedClient} bridges can receive \`${message.internalMessageType}\` messages`,
        )
        return
      }
      processor()
    }

    switch (message.internalMessageType) {
      case 'webBridgeReady':
        if (this.isReady) {
          // Avoid sending duplicate ready messages
          break
        }
        processMessageIfClientIs('native', () => {
          this.sendUnsafeRawMessage({ internalMessageType: 'nativeBridgeReady' })
        })
        break
      case 'nativeBridgeReady':
        if (this.isReady) {
          // Avoid sending duplicate ready messages
          break
        }
        processMessageIfClientIs('web', () => {
          this.sendUnsafeRawMessage({ internalMessageType: 'bothBridgesReady' })
          this.onReady()
        })
        break
      case 'bothBridgesReady':
        if (this.isReady) {
          // Avoid sending duplicate ready messages
          break
        }
        processMessageIfClientIs('native', () => {
          this.onReady()
        })
        break
      default:
        console.warn('[NativeBridge] Received unknown internal message:', message)
    }
  }

  /**
   * Registers a sender function that will be invoked for every message sent.
   * This should be called at the setup step of the native bridge installation
   * flow.
   * @param messageSender A function to dispatch the encoded message. Typically,
   * on web this would be `window.<messagePoster>.postMessage()`, and on native
   * this would be `webViewRef.current?.injectJavaScript()`.
   * @returns An object with a `remove()` method that can be called to stop
   * sending messages via the registered sender.
   */
  addMessageSender(messageSender: (messageContent: object) => void): { remove(): void } {
    const senderId = ++this.senderIdCounter
    this.messageSenders[senderId] = messageSender
    return {
      remove: (): void => {
        delete this.messageSenders[senderId]
      },
    }
  }

  /**
   * Main inlet for raw messages received from the other side. The usage of this
   * method varies across platforms.
   * - On web, it's called by the native sender via `webViewRef.current?.injectJavaScript()`.
   * - On native, it's called by the `onMessage` callback prop of the `WebView` component.
   * @param messageContent Raw message object received from the other side.
   */
  receiveMessage(messageContent: RawMessageContent): void {
    if (typeof messageContent !== 'object' || messageContent === null) {
      console.warn('[NativeBridge] Received invalid message content:', messageContent)
      return
    }

    // Intercept internal messages
    if ('internalMessageType' in messageContent) {
      const internalMessage = messageContent as InternalMessage
      this.processInternalMessage(internalMessage)
      return
    }

    if (!('messageType' in messageContent)) {
      console.warn('[NativeBridge] Received malformed message:', messageContent)
      return
    }

    const { messageType: serializedMessageType, ...unprocessedMessageContent } = messageContent
    const processedMessageContent = this.processMessageContentForReceiving(unprocessedMessageContent) as Omit<
      RawMessageContent,
      'messageType'
    >

    const { originalMessageType: messageType, classification } = deserializeMessageType(serializedMessageType)

    switch (classification) {
      case 'normal':
        this.handleNormalMessage({ ...processedMessageContent, messageType })
        break
      case 'result':
      case 'error':
        this.handleReplyMessage({ ...processedMessageContent, messageType }, classification)
        break
    }
  }

  private async handleNormalMessage(messageContent: RawMessageContent): Promise<void> {
    const { messageType, messageId, payload } = messageContent

    // Broadcast the message to individual listeners, if any. Each of these
    // listener might produce a result, and if so, we need to send a reply.
    const listeners = this.messageListeners[messageType] ?? {}

    if (Object.keys(listeners).length === 0 && Object.keys(this.universalMessageListeners).length === 0) {
      // No listener registered for this message type, exit early.
      return
    }

    let didResolve: boolean = false
    const isReplyExpected = typeof messageId === 'number'

    // These 2 resolvers are for specific message listeners
    const resolveSpecific = isReplyExpected
      ? (...resultFactoryArgs: any[]) => {
          if (didResolve) {
            return
          }
          this.sendResultReply(messageType as any, messageId, ...(resultFactoryArgs as any))
          didResolve = true
        }
      : undefined

    const rejectSpecific = isReplyExpected
      ? (errorPayload: Error | { message: string }) => {
          if (didResolve) {
            return
          }
          this.sendErrorReply(messageType as any, messageId, errorPayload)
          didResolve = true
        }
      : undefined

    // These 2 generic listeners are for universal message listeners
    const resolveGeneric: ReplyWithResultFunction<TManifest> = isReplyExpected
      ? (repliedMessage, ...resultFactoryArgs) => {
          // Filter only the relevant message type
          if (repliedMessage.messageType !== messageType) {
            return
          }
          this.sendResultReply(
            repliedMessage.messageType as any,
            repliedMessage.messageId,
            ...(resultFactoryArgs as any),
          )
          didResolve = true
        }
      : () => {}

    const rejectGeneric: ReplyWithErrorFunction<TManifest> = isReplyExpected
      ? (repliedMessage, errorPayload) => {
          // Filter only the relevant message type
          if (repliedMessage.messageType !== messageType) {
            return
          }
          this.sendErrorReply(repliedMessage.messageType as any, repliedMessage.messageId, errorPayload)
          didResolve = true
        }
      : () => {}

    // Invoke all listeners and collect their results
    try {
      const results = await Promise.all([
        // Collect results from both specific message listeners...
        ...Object.values(listeners).map((listener) =>
          isReplyExpected ? listener(payload, resolveSpecific, rejectSpecific) : listener(payload),
        ),
        // ...and generic message listeners
        ...Object.values(this.universalMessageListeners).map((listener) => {
          listener(messageContent as Values<AllReceivableMessages<TManifest>>, resolveGeneric, rejectGeneric)
        }),
      ])

      if (!didResolve) {
        // Resolve with the first non-undefined result
        for (const result of results) {
          if (Array.isArray(result)) {
            this.sendUnsafeRawMessage({
              messageType: serializeMessageType(messageType, 'result'),
              messageId,
              payload: this.replyMessageFactories[messageType](...result),
            })
            didResolve = true
            break
          }
        }
        // No error thrown here if no result is produced because it could be
        // asynchronously produced by a resolve callback. If no result is
        // eventually produced, the original message handler will time out.
      }
    } catch (error) {
      if (isReplyExpected && !didResolve) {
        this.sendUnsafeRawMessage({
          messageType: serializeMessageType(messageType, 'error'),
          messageId,
          payload: { message: (error as Error)?.message ?? 'Unknown error' },
        })
        didResolve = true
      }
    }
  }

  private handleReplyMessage(
    messageContent: RawMessageContent,
    classification: Exclude<MessageTypeClassification, 'normal'>,
  ): void {
    const { messageType, messageId, payload } = messageContent
    const resolvers = typeof messageId === 'number' ? this.pendingReplyResolvers[messageType]?.[messageId] : undefined
    if (resolvers?.timeout != null) {
      clearTimeout(resolvers?.timeout)
    }

    if (typeof messageId !== 'number' || typeof resolvers !== 'object') {
      // Quit early if there are no resolvers. This could happen if no
      // corresponding message ID were originally sent, or if the message has
      // already been resolved previously and another reply was mistakenly sent.
      return
    }

    switch (classification) {
      case 'result':
        resolvers.resolve(payload)
        break
      case 'error':
        if (
          typeof payload === 'object' &&
          payload !== null &&
          'message' in payload &&
          typeof payload.message === 'string'
        ) {
          resolvers.reject(new Error(payload.message))
        } else {
          resolvers.reject(new Error('Unknown error'))
        }
        break
    }

    delete this.pendingReplyResolvers[messageType][messageId]
  }

  /**
   * Registers a listener for a specific message type.
   * @param messageType A message type defined in the manifest.
   * @param listener A function to be invoked when a message of the specified
   * type is received. The first argument of the function is the payload of the
   * message as returned by the message factory in the manifest.
   * @returns An object with a `remove()` method that can be called to stop
   * handling the message via the registered listener.
   */
  addMessageListener<TMessageType extends keyof GetMessages<TManifest, TClient, 'receiving'>>(
    messageType: TMessageType,
    listener: (
      payload: ReturnType<GetPayloadConstructorFunction<TManifest, TClient, 'receiving', TMessageType, 'normal'>>,
      ...args: MessageListenerDynamicArgs<TManifest, TClient, TMessageType>
    ) => void | Promise<
      Parameters<GetPayloadConstructorFunction<TManifest, TClient, 'receiving', TMessageType, 'result'>>
    >,
  ): { remove(): void } {
    const listenerId = ++this.listenerIdCounter
    const stringMessageType = messageType as string
    this.messageListeners[stringMessageType] = this.messageListeners[stringMessageType] || {}
    this.messageListeners[stringMessageType][listenerId] = listener as (
      payload: any,
      ...args: any[]
    ) => void | Promise<any>
    return {
      remove: (): void => {
        delete this.messageListeners[stringMessageType][listenerId]
      },
    }
  }

  /**
   * Registers a listener for all messages types.
   * @param listener A function to be invoked when any message is received. The
   * first argument of the function is the payload of the message as returned by
   * the message factory in the manifest. The second and third arguments are
   * functions to return a result or an error to a repliable message,
   * respectively.
   * @returns An object with a `remove()` method that can be called to stop
   * handling messages via the registered listener.
   */
  addUniversalMessageListener(
    listener: (
      message: Values<AllReceivableMessages<TManifest>>,
      replyWithResult: ReplyWithResultFunction<TManifest>,
      replyWithError: ReplyWithErrorFunction<TManifest>,
    ) => void,
  ): { remove(): void } {
    const listenerId = ++this.listenerIdCounter
    this.universalMessageListeners[listenerId] = listener
    return {
      remove: (): void => {
        delete this.universalMessageListeners[listenerId]
      },
    }
  }

  /**
   * Dispatches a message via the native bridge.
   * @param messageType A message type defined in the manifest.
   * @param args The arguments to the message factory function that builds the
   * payload of the message to be received on the other side.
   */
  sendMessage<TMessageType extends keyof GetMessages<TManifest, TClient, 'sending'>>(
    messageType: TMessageType,
    ...args: Parameters<GetPayloadConstructorFunction<TManifest, TClient, 'sending', TMessageType, 'normal'>>
  ): TMessageType extends keyof GetRepliableMessages<TManifest, TClient, 'sending'>
    ? Promise<ReturnType<GetPayloadConstructorFunction<TManifest, TClient, 'sending', TMessageType, 'result'>>>
    : void {
    const payloadFactory = this.sendableMessageFactories[messageType as string]

    const contentToSend: RawMessageContent = {
      messageType: messageType as string,
      payload: payloadFactory(...args),
    }

    const requiresReply = this.sendableMessageTypesRequiringReplies.has(messageType as string)

    const messageId = requiresReply ? ++this.messageIdCounter : undefined
    if (typeof messageId === 'number') {
      contentToSend.messageId = messageId
    }

    const pushMessageContent = () => {
      if (this.isReady) {
        this.sendUnsafeRawMessage(contentToSend)
      } else {
        // Block sending messages until the bridge is ready
        this.messageQueue.push(contentToSend)
      }
    }

    if (!requiresReply || typeof messageId !== 'number') {
      return pushMessageContent() as any
    }

    return new Promise((resolve, reject) => {
      const stringMessageType = messageType as string
      this.pendingReplyResolvers[stringMessageType] = this.pendingReplyResolvers[stringMessageType] || {}
      this.pendingReplyResolvers[stringMessageType][messageId] = {
        resolve,
        reject,
        timeout: setTimeout(() => {
          reject(
            new Error(
              `Reply expected for message ${stringMessageType} (id: ${messageId}) but timed out after ${this.options.replyTimeoutInMs}ms`,
            ),
          )
        }, this.options.replyTimeoutInMs),
      }

      pushMessageContent()
    }) as any
  }

  /**
   * Sends a reply to a message received from the other side.
   * @param messageType The message type of the original message.
   * @param args The arguments to the reply factory function that builds the
   * payload of the reply to be received on the other side.
   */
  sendResultReply<TMessageType extends keyof GetRepliableMessages<TManifest, TClient, 'receiving'>>(
    messageType: TMessageType,
    messageId: number,
    ...args: Parameters<GetPayloadConstructorFunction<TManifest, TClient, 'receiving', TMessageType, 'result'>>
  ): void {
    const stringMessageType = messageType as string
    const payloadFactory = this.replyMessageFactories[stringMessageType]
    this.sendUnsafeRawMessage({
      messageType: serializeMessageType(stringMessageType, 'result'),
      messageId,
      payload: payloadFactory(...args),
    })
  }

  /**
   * Sends an error reply to a message received from the other side.
   * @param messageType The message type of the original message.
   * @param error The error to be sent as the reply payload.
   */
  sendErrorReply<TMessageType extends keyof GetRepliableMessages<TManifest, TClient, 'receiving'>>(
    messageType: TMessageType,
    messageId: number,
    error: Error | { message: string },
  ): void {
    const stringMessageType = messageType as string
    this.sendUnsafeRawMessage({
      messageType: serializeMessageType(stringMessageType, 'error'),
      messageId,
      payload: { message: error.message },
    })
  }

  /**
   * Completely resets the bridge, removing all message senders,listeners and
   * states.
   */
  reset(): void {
    this.messageSenders = {}
    this.messageListeners = {}
    Object.values(this.pendingReplyResolvers).forEach((resolversByMessageType) => {
      Object.values(resolversByMessageType).forEach(({ reject, timeout }) => {
        clearTimeout(timeout)
        reject(new Error('Bridge reset'))
      })
    })
    this.pendingReplyResolvers = {}
    this.senderIdCounter = 0
    this.listenerIdCounter = 0
    this.messageIdCounter = 0
    this.isReady = false
    this.messageQueue = []
  }
}

export type { NativeBridgeOptions, RawMessageContent }
export default NativeBridge
